import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
// components
import Iconify from '../iconify';
import { GoogleMapsKey as GOOGLE_MAPS_KEY } from '../../config';

// ----------------------------------------------------------------------

// Optional. When it is missing or rejected we fall back to a keyless embed, so
// the preview is always visible. The key itself lives in src/config.js, shared
// with the Google Places reviews loader.

// Fallback viewport (UAE head office) used when there is nothing to fit.
const DEFAULT_CENTER = { lat: 25.798873, lng: 55.976486 };
const DEFAULT_ZOOM = 12;
const MAP_PADDING = 64;

// How long to wait for the Google Maps script before assuming it failed
// (blocked network, invalid key, quota).
const LOAD_TIMEOUT = 8000;

const mapsSearchUrl = (marker) => `https://www.google.com/maps/search/?api=1&query=${marker.lat},${marker.lng}`;

// Keyless embed — no API key or billing required, so it renders even when the
// JS API is unavailable.
const mapsEmbedUrl = (marker) => `https://www.google.com/maps?q=${marker.lat},${marker.lng}&z=15&output=embed`;

const StyledRoot = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 300,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadiusMd,
  backgroundColor: theme.palette.grey[200],
  [theme.breakpoints.up('sm')]: { height: 360 },
  [theme.breakpoints.up('md')]: { height: 440 },
}));

const OverlayStyle = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  padding: theme.spacing(3),
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.neutral,
  backgroundImage: `radial-gradient(60% 60% at 50% 0%, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 70%)`,
}));

const StyledFrame = styled('iframe')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  border: 0,
});

// ----------------------------------------------------------------------

const Marker = memo(({ label }) => (
  <Stack sx={{ alignItems: 'center', transform: 'translate(-50%, -100%)', pointerEvents: 'none', width: 'max-content' }}>
    {label && (
      <Box
        sx={{
          px: 1,
          py: 0.25,
          mb: 0.5,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1.6,
          whiteSpace: 'nowrap',
          borderRadius: 0.75,
          color: 'common.white',
          bgcolor: '#DF3E30',
          boxShadow: 1,
        }}
      >
        {label}
      </Box>
    )}

    <Iconify icon="material-symbols:location-on" color="#DF3E30" width={36} height={36} />
  </Stack>
));

Marker.propTypes = {
  label: PropTypes.string,
};

// ----------------------------------------------------------------------

function MapState({ children }) {
  return <OverlayStyle spacing={1.5}>{children}</OverlayStyle>;
}

MapState.propTypes = { children: PropTypes.node };

// ----------------------------------------------------------------------

Gmap.propTypes = {
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lat: PropTypes.number,
      lng: PropTypes.number,
      label: PropTypes.string,
    })
  ),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};

export default function Gmap({ markers = [], height, ariaLabel = 'Office locations map', sx }) {
  const mapRef = useRef(null);
  const fittedSignatureRef = useRef(null);
  const userInteractedRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // Drop markers without usable coordinates once, instead of on every render,
  // so the marker set (and its identity) stays stable across re-renders.
  const validMarkers = useMemo(
    () =>
      Array.isArray(markers)
        ? markers.filter((marker) => Number.isFinite(marker?.lat) && Number.isFinite(marker?.lng))
        : [],
    [markers]
  );

  // A signature of the actual data. Re-fitting is keyed off this, so identical
  // data never causes another fit even if the array identity changes.
  const signature = useMemo(
    () => validMarkers.map((marker) => `${marker.id}:${marker.lat}:${marker.lng}`).join('|'),
    [validMarkers]
  );

  const defaultCenter = useMemo(
    () => (validMarkers.length ? { lat: validMarkers[0].lat, lng: validMarkers[0].lng } : DEFAULT_CENTER),
    [validMarkers]
  );

  const canRenderMap = Boolean(GOOGLE_MAPS_KEY) && validMarkers.length > 0 && !hasFailed;

  // Give up on the JS API rather than leaving an empty grey box.
  useEffect(() => {
    if (isReady || hasFailed || !canRenderMap) {
      return undefined;
    }

    const timer = window.setTimeout(() => setHasFailed(true), LOAD_TIMEOUT);
    return () => window.clearTimeout(timer);
  }, [isReady, hasFailed, canRenderMap]);

  // Google calls this global when the key is rejected.
  useEffect(() => {
    const previous = window.gm_authFailure;

    window.gm_authFailure = () => {
      setHasFailed(true);
      if (typeof previous === 'function') {
        previous();
      }
    };

    return () => {
      window.gm_authFailure = previous;
    };
  }, []);

  const fitToMarkers = useCallback(() => {
    const map = mapRef.current?.map;
    const maps = mapRef.current?.maps;

    if (!map || !maps || !validMarkers.length) {
      return;
    }

    // The container can measure as 0x0 on the first paint, which makes
    // fitBounds pick a bogus zoom. Retry on the next frame until it has size.
    const node = typeof map.getDiv === 'function' ? map.getDiv() : null;
    if (node && (node.offsetWidth === 0 || node.offsetHeight === 0)) {
      window.requestAnimationFrame(fitToMarkers);
      return;
    }

    if (validMarkers.length === 1) {
      map.setCenter({ lat: validMarkers[0].lat, lng: validMarkers[0].lng });
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new maps.LatLngBounds();
    validMarkers.forEach((marker) => bounds.extend({ lat: marker.lat, lng: marker.lng }));
    map.fitBounds(bounds, MAP_PADDING);
  }, [validMarkers]);

  const handleApiLoaded = useCallback(
    ({ map, maps }) => {
      mapRef.current = { map, maps };
      setIsReady(true);

      // Once the reader takes control, stop re-fitting under them.
      map.addListener('dragstart', () => {
        userInteractedRef.current = true;
      });

      fitToMarkers();
      fittedSignatureRef.current = signature;
    },
    [fitToMarkers, signature]
  );

  // Re-fit only when the underlying data changes and the reader hasn't moved
  // the map themselves.
  useEffect(() => {
    if (!mapRef.current || userInteractedRef.current) {
      return;
    }

    if (fittedSignatureRef.current === signature) {
      return;
    }

    fitToMarkers();
    fittedSignatureRef.current = signature;
  }, [signature, fitToMarkers]);

  // Nothing to show a map for.
  if (validMarkers.length === 0) {
    return (
      <StyledRoot sx={{ height, ...sx }} role="region" aria-label={ariaLabel}>
        <MapState>
          <Iconify icon="material-symbols:location-on" width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
            Location map unavailable
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
            Our office locations will appear here once they are available.
          </Typography>
        </MapState>
      </StyledRoot>
    );
  }

  // JS API unavailable (no key, rejected key, blocked network, quota): show the
  // keyless embed for the primary office so a preview is still visible, and
  // keep the other offices one tap away.
  if (!canRenderMap) {
    const [primary] = validMarkers;

    return (
      <StyledRoot sx={{ height, ...sx }} role="region" aria-label={ariaLabel}>
        <StyledFrame
          title={`Map of ${primary.label ?? 'our office'}`}
          src={mapsEmbedUrl(primary)}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />

        {validMarkers.length > 1 && (
          <Stack
            direction="row"
            useFlexGap
            spacing={1}
            sx={{ flexWrap: 'wrap', position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 2 }}
          >
            {validMarkers.map((marker) => (
              <Button
                key={marker.id}
                href={mapsSearchUrl(marker)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<Iconify icon="mdi:google-maps" sx={{ width: 18, height: 18 }} />}
                sx={{ bgcolor: 'background.paper', boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
              >
                {marker.label ?? 'Open in Google Maps'}
              </Button>
            ))}
          </Stack>
        )}
      </StyledRoot>
    );
  }

  return (
    <StyledRoot sx={{ height, ...sx }} role="region" aria-label={ariaLabel}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: GOOGLE_MAPS_KEY }}
        defaultCenter={defaultCenter}
        defaultZoom={DEFAULT_ZOOM}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
      >
        {validMarkers.map((marker) => (
          <Marker key={marker.id} lat={marker.lat} lng={marker.lng} label={marker.label} />
        ))}
      </GoogleMapReact>

      {!isReady && (
        <MapState>
          <CircularProgress size={28} thickness={4} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading map…
          </Typography>
        </MapState>
      )}
    </StyledRoot>
  );
}
